// src/pages/AdminOrganizationManagement.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import axiosClient from '../services/api';
import { useToast } from '../components/ToastContainer';

const AdminOrganizationManagement = () => {
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [offerModal, setOfferModal] = useState({ open: false, orgId: null, orgName: '' });
    const [viewOffersModal, setViewOffersModal] = useState({ open: false, orgId: null, orgName: '', offers: [] });
    const [editOfferModal, setEditOfferModal] = useState({ open: false, offer: null, loading: false });
    const [updateOrgModal, setUpdateOrgModal] = useState({ open: false, org: null, loading: false });
    const [offerData, setOfferData] = useState({
        organizationId: '',
        courseIds: [],
        discountType: 'percentage',
        discountValue: '',
        seatCount: '',
        expiresAt: ''
    });
    const [allCourses, setAllCourses] = useState([]);
    const [orgUpdateData, setOrgUpdateData] = useState({
        companyName: '',
        contactPerson: '',
        industry: '',
        employeeCount: ''
    });
    const [editOfferData, setEditOfferData] = useState({
        courseIds: [],
        discountType: 'percentage',
        discountValue: '',
        seatCount: '',
        expiresAt: ''
    });
    const toast = useToast();

    useEffect(() => {
        fetchOrganizations();
        fetchCourses();
    }, []);

    const fetchOrganizations = async () => {
        try {
            const res = await axiosClient.get('/org_admin/organizations');
            setOrganizations(res.data.organizations);
        } catch (err) {
            toast.error('Failed to load organizations');
        } finally {
            setLoading(false);
        }
    };

    const fetchCourses = async () => {
        try {
            const res = await axiosClient.get('/courses');
            setAllCourses(res.data.data.filter(c => c.status === 'approved'));
        } catch (err) {
            toast.error('Failed to load courses');
        }
    };

    const fetchOrganizationOffers = async (orgId) => {
        try {
            const couponRes = await axiosClient.get('/coupons');
            const orgOffers = couponRes.data.coupons.filter(offer => offer.organizationId === orgId);
            return orgOffers;
        } catch (err) {
            toast.error('Failed to load offers for organization');
            return [];
        }
    };

    const handleCreateOffer = async () => {
        if (!offerData.courseIds.length || !offerData.discountValue || !offerData.seatCount) {
            toast.error('Please fill all required fields');
            return;
        }

        try {
            await axiosClient.post(`/org_admin/organizations/offers`, {
                ...offerData,
                organizationId: offerModal.orgId,
                courseIds: offerData.courseIds,
                discountType: offerData.discountType,
                discountValue: parseFloat(offerData.discountValue),
                seatCount: parseInt(offerData.seatCount)
            });
            toast.success('Offer created successfully');

            setOfferModal({ open: false, orgId: null, orgName: '' });
            setOfferData({
                organizationId: '',
                courseIds: [],
                discountType: 'percentage',
                discountValue: '',
                seatCount: '',
                expiresAt: ''
            });
            fetchOrganizations();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to create offer');
        }
    };

    const openViewOffersModal = async (org) => {
        const offers = await fetchOrganizationOffers(org._id);
        setViewOffersModal({ open: true, orgId: org._id, orgName: org.companyName, offers });
    };

    const openEditOfferModal = (offer) => {
        setEditOfferData({
            courseIds: offer.courses.map(c => c._id),
            discountType: offer.discountType,
            discountValue: offer.discountValue,
            seatCount: offer.maxUsesPerOrg,
            expiresAt: offer.expiresAt ? new Date(offer.expiresAt).toISOString().split('T')[0] : ''
        });
        setEditOfferModal({ open: true, offer, loading: false });
    };

    const handleUpdateOffer = async () => {
        if (!editOfferModal.offer) return;
        if (!editOfferData.courseIds.length || !editOfferData.discountValue || !editOfferData.seatCount) {
            toast.error('Please fill all required fields');
            return;
        }
        setEditOfferModal(prev => ({ ...prev, loading: true }));
        try {
            await axiosClient.put(`/org_admin/organizations/offers/${editOfferModal.offer._id}`, {
                ...editOfferData,
                discountValue: parseFloat(editOfferData.discountValue),
                seatCount: parseInt(editOfferData.seatCount)
            });
            toast.success('Offer updated successfully');
            setEditOfferModal({ open: false, offer: null, loading: false });
            const updatedOffers = viewOffersModal.offers.map(o => o._id === editOfferModal.offer._id ? { ...o, ...editOfferData, maxUsesPerOrg: parseInt(editOfferData.seatCount) } : o);
            setViewOffersModal(prev => ({ ...prev, offers: updatedOffers }));
            fetchOrganizations();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to update offer');
        } finally {
            setEditOfferModal(prev => ({ ...prev, loading: false }));
        }
    };

    const openUpdateOrgModal = (org) => {
        setOrgUpdateData({
            companyName: org.companyName || '',
            contactPerson: org.contactPerson || '',
            industry: org.industry || '',
            customIndustry: org.customIndustry || '',
            employeeCount: org.employeeCount || ''
        });
        setUpdateOrgModal({ open: true, org, loading: false });
    };

    const handleUpdateOrganization = async () => {
        if (!updateOrgModal.org) return;
        if (!orgUpdateData.companyName || !orgUpdateData.contactPerson) {
            toast.error('Company Name and Contact Person are required');
            return;
        }
        setUpdateOrgModal(prev => ({ ...prev, loading: true }));
        try {
            const res = await axiosClient.put(`/org_admin/organizations/${updateOrgModal.org._id}`, orgUpdateData);
            toast.success('Organization updated successfully');
            setUpdateOrgModal({ open: false, org: null, loading: false });
            setOrganizations(prev => prev.map(org => org._id === updateOrgModal.org._id ? { ...org, ...res.data.user } : org));
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to update organization');
        } finally {
            setUpdateOrgModal(prev => ({ ...prev, loading: false }));
        }
    };


    if (loading) {
        return (
            <DashboardLayout userRole="admin">
                <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout userRole="admin">
            <div className="max-w-5xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">Organization Management</h1>
                    <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                        Manage organizations, their employees, and course offers.
                    </p>
                </div>

                {organizations.length > 0 ? (
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                                <thead className="bg-neutral-50 dark:bg-neutral-700">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Organization</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Contact</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Industry</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Employees</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Wallet</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                                    {organizations.map(org => (
                                        <tr key={org._id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="font-medium text-neutral-900 dark:text-white">
                                                    {org.companyName}
                                                </div>
                                                <div className="text-sm text-neutral-600 dark:text-neutral-400">
                                                    {org.email}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-neutral-900 dark:text-white">
                                                {org.contactPerson}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-neutral-900 dark:text-white">
                                                {org.industry || '—'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-neutral-900 dark:text-white">
                                                {org.employeeCount || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-neutral-900 dark:text-white">
                                                ₦{org.walletBalance?.toLocaleString() || '0.00'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                                                <button
                                                    onClick={() => setOfferModal({ open: true, orgId: org._id, orgName: org.companyName })}
                                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                                                >
                                                    Create Offer
                                                </button>
                                                <button
                                                    onClick={() => openViewOffersModal(org)}
                                                    className="text-green-600 hover:text-green-800 dark:text-green-400"
                                                >
                                                    View Offers
                                                </button>
                                                <button
                                                    onClick={() => openUpdateOrgModal(org)}
                                                    className="text-yellow-600 hover:text-yellow-800 dark:text-yellow-400"
                                                >
                                                    Update Info
                                                </button>
                                                <Link
                                                    to={`/admin/cloud/analytics/${org.companyName}/${org._id}`}
                                                    state={{ entityName: org.companyName, entityType: org.role }}
                                                    className="text-primary-600 hover:text-primary-800 dark:text-primary-400"
                                                >
                                                    View Analytics
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700">
                        <div className="text-6xl mb-4">📚</div>
                        <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No Registered Organization Yet </h3>
                        <p className="text-neutral-600 dark:text-neutral-400">
                            you will start seeing data once organization schema has data
                        </p>
                    </div>
                )}
            </div>

            {/* Offer Modal */}
            {offerModal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg w-full max-w-md">
                        <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
                            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                                Create Course Offer for {offerModal.orgName}
                            </h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Course
                                </label>
                                <select
                                    multiple
                                    value={offerData.courseIds}
                                    onChange={(e) => setOfferData({ ...offerData, courseIds: Array.from(e.target.selectedOptions).map(opt => opt.value) })}
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    {allCourses.map(course => (
                                        <option key={course._id} value={course._id}>
                                            {course.title} (₦{course.price.toLocaleString()})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                        Discount Type
                                    </label>
                                    <select
                                        value={offerData.discountType}
                                        onChange={(e) => setOfferData({ ...offerData, discountType: e.target.value })}
                                        className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="percentage">Percentage</option>
                                        <option value="fixed">Fixed Amount</option>
                                        <option value="free">Free</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                        Discount Value
                                    </label>
                                    <input
                                        type="number"
                                        value={offerData.discountValue}
                                        onChange={(e) => setOfferData({ ...offerData, discountValue: e.target.value })}
                                        placeholder="e.g., 50 (for 50%) or 10000 (for ₦10,000 off)"
                                        className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Number of Seats
                                </label>
                                <input
                                    type="number"
                                    value={offerData.seatCount}
                                    onChange={(e) => setOfferData({ ...offerData, seatCount: e.target.value })}
                                    placeholder="e.g., 50"
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Expiry Date (Optional)
                                </label>
                                <input
                                    type="date"
                                    value={offerData.expiresAt}
                                    onChange={(e) => setOfferData({ ...offerData, expiresAt: e.target.value })}
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-2">
                            <button
                                onClick={() => setOfferModal({ open: false, orgId: null, orgName: '' })}
                                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateOffer}
                                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg"
                            >
                                Create Offer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Offers Modal */}
            {viewOffersModal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
                            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                                Offers for {viewOffersModal.orgName}
                            </h3>
                        </div>
                        <div className="p-6">
                            {viewOffersModal.offers.length === 0 ? (
                                <p>No offers found for this organization.</p>
                            ) : (
                                <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                                    <thead className="bg-neutral-50 dark:bg-neutral-700">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Code</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Type</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Value</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Seats</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Used</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Expiry</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                                        {viewOffersModal.offers.map(offer => (
                                            <tr key={offer._id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-white">{offer.code}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-white capitalize">{offer.discountType}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-white">
                                                    {offer.discountType === 'free' ? 'FREE' : offer.discountType === 'percentage' ? `${offer.discountValue}%` : `₦${offer.discountValue}`}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-white">{offer.maxUsesPerOrg}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-white">{offer.seatsUsed}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-white">
                                                    {offer.expiresAt ? new Date(offer.expiresAt).toLocaleDateString() : 'Never'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-white">
                                                    <button
                                                        onClick={() => openEditOfferModal(offer)}
                                                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 mr-2"
                                                    >
                                                        Edit
                                                    </button>
                                                    {/* Add delete button if needed */}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-end">
                            <button
                                onClick={() => setViewOffersModal({ open: false, orgId: null, orgName: '', offers: [] })}
                                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Offer Modal */}
            {editOfferModal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg w-full max-w-md">
                        <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
                            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                                Edit Offer: {editOfferModal.offer?.code}
                            </h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Course
                                </label>
                                <select
                                    multiple
                                    value={editOfferData.courseIds}
                                    onChange={(e) => setEditOfferData({ ...editOfferData, courseIds: Array.from(e.target.selectedOptions).map(opt => opt.value) })}
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    {allCourses.map(course => (
                                        <option key={course._id} value={course._id}>
                                            {course.title} (₦{course.price.toLocaleString()})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                        Discount Type
                                    </label>
                                    <select
                                        value={editOfferData.discountType}
                                        onChange={(e) => setEditOfferData({ ...editOfferData, discountType: e.target.value })}
                                        className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="percentage">Percentage</option>
                                        <option value="fixed">Fixed Amount</option>
                                        <option value="free">Free</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                        Discount Value
                                    </label>
                                    <input
                                        type="number"
                                        value={editOfferData.discountValue}
                                        onChange={(e) => setEditOfferData({ ...editOfferData, discountValue: e.target.value })}
                                        placeholder="e.g., 50 (for 50%) or 10000 (for ₦10,000 off)"
                                        className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Number of Seats
                                </label>
                                <input
                                    type="number"
                                    value={editOfferData.seatCount}
                                    onChange={(e) => setEditOfferData({ ...editOfferData, seatCount: e.target.value })}
                                    placeholder="e.g., 50"
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Expiry Date (Optional)
                                </label>
                                <input
                                    type="date"
                                    value={editOfferData.expiresAt}
                                    onChange={(e) => setEditOfferData({ ...editOfferData, expiresAt: e.target.value })}
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-2">
                            <button
                                onClick={() => setEditOfferModal({ open: false, offer: null, loading: false })}
                                disabled={editOfferModal.loading}
                                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateOffer}
                                disabled={editOfferModal.loading}
                                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg disabled:opacity-50"
                            >
                                {editOfferModal.loading ? 'Updating...' : 'Update Offer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Update Organization Modal */}
            {updateOrgModal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg w-full max-w-md">
                        <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
                            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                                Update Organization: {updateOrgModal.org?.companyName}
                            </h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Company Name *
                                </label>
                                <input
                                    type="text"
                                    value={orgUpdateData.companyName}
                                    onChange={(e) => setOrgUpdateData({ ...orgUpdateData, companyName: e.target.value })}
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Contact Person *
                                </label>
                                <input
                                    type="text"
                                    value={orgUpdateData.contactPerson}
                                    onChange={(e) => setOrgUpdateData({ ...orgUpdateData, contactPerson: e.target.value })}
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Industry
                                </label>
                                <select
                                    value={orgUpdateData.industry}
                                    onChange={(e) => {
                                        const selected = e.target.value;
                                        setOrgUpdateData({
                                            ...orgUpdateData, industry: selected,
                                            customIndustry: selected === "Other" ? orgUpdateData.customIndustry : "",
                                        });

                                    }}
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    <option value="">Select Industry</option>
                                    <option value="oil-gas">Oil & Gas</option>
                                    <option value="energy-power">Energy & Power</option>
                                    <option value="construction-real-estate">Construction & Real Estate</option>
                                    <option value="manufacturing">Manufacturing</option>
                                    <option value="agriculture-agribusiness">Agriculture & Agribusiness</option>
                                    <option value="mining-solid-minerals">Mining & Solid Minerals</option>
                                    <option value="information-technology">Information Technology (IT) & Software</option>
                                    <option value="telecommunications">Telecommunications</option>
                                    <option value="financial-services">Financial Services (Banking, Fintech, Insurance)</option>
                                    <option value="transportation-logistics">Transportation & Logistics</option>
                                    <option value="education-training">Education & Training</option>
                                    <option value="healthcare-pharmaceuticals">Healthcare & Pharmaceuticals</option>
                                    <option value="media-entertainment">Media & Entertainment</option>
                                    <option value="retail-ecommerce">Retail & E-commerce</option>
                                    <option value="hospitality-tourism">Hospitality & Tourism</option>
                                    <option value="security-defense">Security & Defense</option>
                                    <option value="legal-consulting">Legal & Consulting Services</option>
                                    <option value="public-sector-government">Public Sector & Government</option>
                                    <option value="environmental-services">Environmental Services</option>
                                    <option value="aviation-aerospace">Aviation & Aerospace</option>
                                    <option value="marine-shipping">Marine & Shipping</option>
                                    <option value="textile-fashion">Textile & Fashion</option>
                                    <option value="food-beverages">Food & Beverages</option>
                                    <option value="nonprofit-ngos">Nonprofit & NGOs</option>
                                    <option value="sports-recreation">Sports & Recreation</option>
                                    <option value="other">Other</option>
                                </select>

                                {orgUpdateData.industry === "other" && (
                                    <input
                                        type="text"
                                        placeholder="Please specify your industry"
                                        value={orgUpdateData.customIndustry}
                                        onChange={(e) =>
                                            setOrgUpdateData({ ...orgUpdateData, customIndustry: e.target.value, })
                                        }
                                        className="mt-2 w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                    Employee Count
                                </label>
                                <input
                                    type="number"
                                    value={orgUpdateData.employeeCount}
                                    onChange={(e) => setOrgUpdateData({ ...orgUpdateData, employeeCount: e.target.value })}
                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                        </div>
                        <div className="p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-2">
                            <button
                                onClick={() => setUpdateOrgModal({ open: false, org: null, loading: false })}
                                disabled={updateOrgModal.loading}
                                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateOrganization}
                                disabled={updateOrgModal.loading}
                                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg disabled:opacity-50"
                            >
                                {updateOrgModal.loading ? 'Updating...' : 'Update Organization'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default AdminOrganizationManagement;